import type { FC } from 'react'
import styled from 'styled-components'

import { type SocialShareButtonGroupProps, SocialShareButtonGroup } from '@/components/blocks/SocialShareButtonGroup'
import { Container } from '@/components/shared/Container'
import { TrackedLink } from '@/components/shared/TrackedLink'

import { DatoImage } from '@/elements/global/DatoImage'

import { formatDisplayDate } from '@/lib/utils/dateFormatting'

import type { AcademyArticle } from '../AcademyContentPage'
import { ArchiveMessage } from './ArchiveMessage'

const StyledArticleHeader = styled.div`
  padding-left: 100px;
  padding-top: 48px;
  margin-top: 0;
  h1 {
    max-width: 630px;
    font-family: ${(props) => props.theme.v2.font.nantes};
  }
  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    padding-left: 0;
    margin-top: 0;
    h1 {
      max-width: 100%;
    }
  }
`
const StyledAuthorRow = styled.div`
  display: flex;
  margin-top: 50px;
  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    flex-direction: column-reverse;
    margin-top: 30px;
  }
`
const StyledAuthor = styled.div`
  margin-right: 100px;
  a {
    display: flex;
    text-decoration: none;
    align-items: center;
    color: ${(props) => props.theme.v2.onSurface.text};
  }
  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    margin-right: 0;
    &:last-child {
      margin-bottom: 22px;
    }
  }
`
const StyledAuthorImage = styled.div`
  margin-right: 18px;
  width: 82px;
  height: 82px;
  border-radius: 50%;
  overflow: hidden;
  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    width: 56px;
    height: 56px;
  }
`
const StyledAuthorText = styled.div`
  flex: 1;
  span,
  strong {
    display: block;
  }
  span {
    color: ${(props) => props.theme.v2.text.secondary};
  }
  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    font-size: 14px;
    line-height: 20px;
  }
`

const StyledSocialShareButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 48px;
  p {
    margin-right: 16px;
    font-size: ${(props) => props.theme.v2.typography.body.xs.fontSize};
    line-height: ${(props) => props.theme.v2.typography.body.xs.lineHeight};
    font-family: ${(props) => props.theme.v2.font.lotaGrotesqueLight};
  }
  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    margin-top: 32px;
  }
`
const SocialContainer: FC<SocialShareButtonGroupProps> = ({ twitterUrl, facebookUrl, mailToUrl, toPrint }) => {
  return (
    <StyledSocialShareButtonsContainer>
      <p>Share this article</p>
      <SocialShareButtonGroup
        twitterUrl={twitterUrl}
        facebookUrl={facebookUrl}
        mailToUrl={mailToUrl}
        toPrint={toPrint}
      />
    </StyledSocialShareButtonsContainer>
  )
}

export const ArticleHeader: FC<{ article: AcademyArticle }> = ({ article }) => {
  const reviewerUrl =
    !article.reviewer?.reviewer && article.reviewer ? `/learn/${article.reviewer.slug}` : '/learn/reviewers/'

  const publishedDateHeading =
    article._firstPublishedAt?.substring(10) === article._publishedAt?.substring(10) ? 'Published' : 'Updated'

  const { articleSlug, articleTitle } = article
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=https://zoe.com/learn/${articleSlug}`
  const twitterUrl = `https://twitter.com/intent/tweet?url=https://zoe.com/learn/${articleSlug}`
  const mailToUrl = `mailto:?subject=${articleTitle}&body=https://zoe.com/learn/${articleSlug}`

  return (
    <Container>
      <StyledArticleHeader>
        {article._publishedAt && (
          <p className="small">
            {publishedDateHeading} <strong>{formatDisplayDate(article._publishedAt)}</strong>
          </p>
        )}
        {article.enableArchive && <ArchiveMessage updatedDate={article._updatedAt} />}
        <h1>{article.articleTitle}</h1>
        <StyledAuthorRow>
          <StyledAuthor className="author">
            <TrackedLink href={`/learn/${article.author.slug}`} passHref>
              {article.author.authorImage.responsiveImage && (
                <StyledAuthorImage>
                  <DatoImage data={article.author.authorImage.responsiveImage} />
                </StyledAuthorImage>
              )}
              <StyledAuthorText>
                <span>Written by</span>
                <strong>
                  {article.author.name}
                  {article.author.qualifications && <>, {article.author.qualifications}</>}
                </strong>
              </StyledAuthorText>
            </TrackedLink>
          </StyledAuthor>
          {article.reviewer && (
            <StyledAuthor>
              <TrackedLink href={reviewerUrl} passHref>
                {article.reviewer.authorImage.responsiveImage && (
                  <StyledAuthorImage>
                    <DatoImage data={article.reviewer.authorImage.responsiveImage} layout="responsive" />
                  </StyledAuthorImage>
                )}
                <StyledAuthorText>
                  <span>Reviewed by</span>
                  <strong>
                    {article.reviewer.name}
                    {article.reviewer.qualifications && <>, {article.reviewer.qualifications}</>}
                  </strong>
                </StyledAuthorText>
              </TrackedLink>
            </StyledAuthor>
          )}
        </StyledAuthorRow>
        <SocialContainer twitterUrl={twitterUrl} facebookUrl={facebookUrl} mailToUrl={mailToUrl} toPrint={true} />
      </StyledArticleHeader>
    </Container>
  )
}
